import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ArrowLeft, Save, Eye, Plus, X, Loader2, Settings } from "lucide-react";
import { Link } from "react-router-dom";
import { blogAPI } from "../services/api";
import { useToast } from "../hooks/useToast";
import { generateSlug, calculateReadTime, markdownToHtml } from "../lib/utils";
import RichTextEditor from "../components/RichTextEditor";

const CreateBlogPage = () => {
  const navigate = useNavigate();
  const { id: blogId } = useParams();
  const { toast } = useToast();

  // Determine if we're in edit mode based on route
  const isEditMode = window.location.pathname.includes('/blog/edit/');

  // State for blog data and edit mode
  const [editMode, setEditMode] = useState(isEditMode);
  const [blogData, setBlogData] = useState(null);

  const [formData, setFormData] = useState({
    title: "",
    excerpt: "",
    content: "",
    tags: [],
    author: "MyFinBrain Team",
    visibility: "internal",
    featuredImage: null,
    media: [],
    seo: {
      metaTitle: "",
      metaDescription: "",
      keywords: []
    }
  });
  const [newTag, setNewTag] = useState("");
  const [previewMode, setPreviewMode] = useState(false);
  const [previewSlug, setPreviewSlug] = useState("");
  const [availableTags, setAvailableTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // Load initial data function
  const loadInitialData = async () => {
    try {
      const tagsResponse = await blogAPI.getTags();

      if (tagsResponse.success) {
        setAvailableTags(tagsResponse.data.map(tag => tag.name || tag));
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast.error("Failed to load tags");
    }
  };

  // Load existing blog data if editing
  const loadExistingBlog = async (id) => {
    try {
      const response = await blogAPI.getBlogById(id);
      if (response.success) {
        setBlogData(response.data);
        setEditMode(true);
        // Populate form with existing data
        setFormData({
          title: response.data.title || "",
          excerpt: response.data.excerpt || "",
          content: response.data.content || "",
          tags: response.data.tags || [],
          author: response.data.author || "MyFinBrain Team",
          visibility: response.data.visibility || "internal",
          featuredImage: response.data.featuredImage || null,
          media: response.data.media || [],
          seo: {
            metaTitle: response.data.seo?.metaTitle || "",
            metaDescription: response.data.seo?.metaDescription || "",
            keywords: response.data.seo?.keywords || []
          }
        });
      } else {
        toast.error("Blog not found");
        navigate('/admin');
      }
    } catch (error) {
      console.error("Error loading blog:", error);
      toast.error("Failed to load blog");
      navigate('/admin');
    } finally {
      setInitialLoading(false);
    }
  };

  // Generate preview slug when title changes
  useEffect(() => {
    if (formData.title) {
      setPreviewSlug(generateSlug(formData.title));
    } else {
      setPreviewSlug("");
    }
  }, [formData.title]);

  // Load categories and tags on component mount
  useEffect(() => {
    loadInitialData();
    // If in edit mode and we have a blog ID, load existing blog
    if (isEditMode && blogId) {
      loadExistingBlog(blogId);
    } else {
      setInitialLoading(false);
    }
  }, [blogId, isEditMode]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let response;

      if (editMode && blogData) {
        // Update existing blog
        response = await blogAPI.updateBlog(blogId, formData);
      } else {
        // Create new blog - let API generate ID and handle metadata
        const blogDataToSubmit = {
          ...formData,
          slug: previewSlug,
          readTime: calculateReadTime(formData.content),
          // Auto-fill SEO if not provided
          seo: {
            metaTitle: formData.seo.metaTitle || formData.title,
            metaDescription: formData.seo.metaDescription || formData.excerpt,
            keywords: formData.seo.keywords.length > 0 ? formData.seo.keywords : formData.tags
          }
        };

        response = await blogAPI.createBlog(blogDataToSubmit);
      }

      if (response.success) {
        const successMessage = editMode ? "Blog post updated successfully!" : "Blog post created successfully!";
        toast.success(successMessage);

        // Navigate to the actual blog post or blog listing
        if (response.data && response.data.slug) {
          navigate(`/blog/${response.data.slug}`);
        } else if (response.data && response.data.id) {
          navigate(`/blog/${response.data.id}`);
        } else {
          navigate("/blog");
        }
      } else {
        toast.error(response.error?.message || "Failed to save blog post");
      }
    } catch (error) {
      console.error("Error saving blog:", error);
      toast.error(editMode ? "Failed to update blog post" : "Failed to create blog post");
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = () => {
    setPreviewMode(!previewMode);
  };



  if (initialLoading) {
    return (
      <div className="min-h-screen bg-slate-50 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-2 text-slate-600">Loading editor...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <Link to="/blog" className="inline-flex items-center text-blue-600 hover:text-blue-700 transition-colors duration-200">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Link>
            <Link to="/admin" className="inline-flex items-center text-slate-600 hover:text-slate-700 transition-colors duration-200 text-sm">
              <Settings className="w-4 h-4 mr-1" />
              Admin Dashboard
            </Link>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2">
                {editMode ? "Edit Blog Post" : "Create New Blog Post"}
              </h1>

              {/* URL Preview */}
              {previewSlug && !editMode && (
                <div className="text-sm text-slate-600 mb-4 sm:mb-0">
                  <span className="font-medium">URL Preview:</span>{" "}
                  <code className="bg-slate-100 px-2 py-1 rounded text-slate-800">
                    /blog/{previewSlug}
                  </code>
                </div>
              )}
            </div>

            <div className="flex space-x-3">
              <Button
                onClick={handlePreview}
                variant="outline"
                className="flex items-center"
                disabled={!formData.title || !formData.content}
              >
                <Eye className="w-4 h-4 mr-2" />
                {previewMode ? "Edit" : "Preview"}
              </Button>

              <Button
                onClick={handleSubmit}
                disabled={loading || !formData.title || !formData.content}
                className="flex items-center"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                {editMode ? "Update Post" : "Create Blog Post"}
              </Button>
            </div>
          </div>
        </div>

        {!previewMode ? (
          /* Edit Mode */
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Post Content</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Title *
                    </label>
                    <Input
                      type="text"
                      value={formData.title}
                      onChange={(e) => handleInputChange("title", e.target.value)}
                      placeholder="Enter your blog post title..."
                      className="text-lg"
                      disabled={loading}
                    />
                  </div>

                  {/* Excerpt */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Excerpt *
                    </label>
                    <Textarea
                      value={formData.excerpt}
                      onChange={(e) => handleInputChange("excerpt", e.target.value)}
                      placeholder="Write a brief description of your post..."
                      rows={3}
                      disabled={loading}
                    />
                  </div>

                  {/* Content */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Content *
                    </label>
                    <RichTextEditor
                      value={formData.content}
                      onChange={(content) => handleInputChange("content", content)}
                      placeholder="Write your blog post content here..."
                      blogId={blogId}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Publishing Settings */}
              <Card>
                <CardHeader>
                  <CardTitle>Publishing Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">


                  {/* Visibility */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Visibility *
                    </label>
                    <Select
                      value={formData.visibility}
                      onValueChange={(value) => handleInputChange("visibility", value)}
                      disabled={loading}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select visibility" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="public">Public - Visible to all visitors</SelectItem>
                        <SelectItem value="internal">Internal - Only visible to admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Author */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Author
                    </label>
                    <Input
                      type="text"
                      value={formData.author}
                      onChange={(e) => handleInputChange("author", e.target.value)}
                      placeholder="Author name"
                      disabled={loading}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Tags */}
              <Card>
                <CardHeader>
                  <CardTitle>Tags</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Add new tag */}
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      placeholder="Add a tag..."
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                      disabled={loading}
                    />
                    <Button onClick={handleAddTag} size="sm" disabled={loading}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Current tags */}
                  {formData.tags.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-slate-700">Current Tags:</p>
                      <div className="flex flex-wrap gap-2">
                        {formData.tags.map((tag) => (
                          <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                            {tag}
                            <button
                              onClick={() => handleRemoveTag(tag)}
                              className="hover:text-red-600 transition-colors duration-200"
                              disabled={loading}
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Suggested tags */}
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-slate-700">Suggested Tags:</p>
                    <div className="flex flex-wrap gap-2">
                      {availableTags
                        .filter(tag => !formData.tags.includes(tag))
                        .slice(0, 8)
                        .map((tag) => (
                          <button
                            key={tag}
                            onClick={() => setFormData(prev => ({ ...prev, tags: [...prev.tags, tag] }))}
                            className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 px-2 py-1 rounded transition-colors duration-200 disabled:opacity-50"
                            disabled={loading}
                          >
                            + {tag}
                          </button>
                        ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          /* Preview Mode */
          <div className="max-w-4xl mx-auto">
            <Card>
              <CardContent className="p-8">
                {/* Preview Header */}
                <div className="mb-8 pb-8 border-b border-slate-200">
                  <h1 className="text-4xl font-bold text-slate-900 mb-4">
                    {formData.title || "Your Blog Title"}
                  </h1>

                  {formData.excerpt && (
                    <p className="text-xl text-slate-600 mb-6">
                      {formData.excerpt}
                    </p>
                  )}

                  <div className="flex items-center gap-6 text-sm text-slate-500">
                    <span>By {formData.author}</span>
                    <span>{new Date().toLocaleDateString()}</span>
                    <span>5 min read</span>
                  </div>

                  {formData.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4">
                      {formData.tags.map((tag) => (
                        <Badge key={tag} variant="outline">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Preview Content */}
                {formData.content ? (
                  <div
                    className="prose prose-lg prose-slate max-w-none prose-headings:text-slate-900 prose-p:text-slate-700 prose-li:text-slate-700 prose-blockquote:text-slate-600 prose-a:text-blue-600 hover:prose-a:text-blue-800"
                    dangerouslySetInnerHTML={{
                      __html: markdownToHtml(formData.content)
                    }}
                  />
                ) : (
                  <div className="prose prose-lg max-w-none">
                    <p className="text-slate-400 italic">Your content will appear here...</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateBlogPage;